import React, { useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import {
  DeleteAvatarButton,
  CircleContainer,
  ImageConstraintsText,
  ImageDropzoneContainer,
  ImageIconContainer,
  ImageUploadContainer,
  ImageUploadInstructionText,
  PlusIconWrapper
} from '../../components/ImageUploadWithPreviewV3';
import ImageIcon from '../../components/icons/ImageIcon';
import PlusIcon from '../../components/icons/PlusIcon';
import { FormField, FormItem, FormMessage } from '../../components/ui/Form';
import { compressImgFile, ImagesOptions, imageString } from '../../helper';
import { formatMbFileSize } from '../../utils/utils';
import { MediaKeys } from '../offers/utils';
import {
  DOCUMENT_THUMBNAIL_ACCEPTED_MIME_TYPES,
  DOCUMENT_THUMBNAIL_MAX_FILE_SIZE,
  DOCUMENT_THUMBNAIL_MAX_FILE_SIZE_ERROR
} from './constants';
import DocumentUploadedFileRow from './DocumentUploadedFileRow';
import { DocumentSection, DocumentSectionHeading } from './documentWizardStyles';

const THUMBNAIL_EXTENSIONS = ['.jpg', '.jpeg', '.png'];

const isDocumentThumbnailFile = (file) => {
  if (!file?.name) {
    return false;
  }
  const lowerName = file.name.toLowerCase();
  return THUMBNAIL_EXTENSIONS.some((ext) => lowerName.endsWith(ext));
};

const getThumbnailDisplayName = (url) => {
  const trimmed = String(url || '').trim();
  if (!trimmed) {
    return '';
  }
  const segment = trimmed.split('/').pop()?.split('?')[0];
  return segment || trimmed;
};

const resolveDocumentThumbnailUrl = (raw) => {
  const trimmed = String(raw || '').trim();
  if (!trimmed) {
    return '';
  }
  return trimmed.startsWith('http') ? trimmed : imageString(trimmed);
};

function ThumbnailDropzone({ form, t, remountKey }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const { setValue, setError, clearErrors } = form;

  const { getRootProps, getInputProps } = useDropzone({
    accept: DOCUMENT_THUMBNAIL_ACCEPTED_MIME_TYPES,
    maxSize: DOCUMENT_THUMBNAIL_MAX_FILE_SIZE,
    multiple: false,
    disabled: isProcessing,
    onDrop: async (acceptedFiles, fileRejections) => {
      if (fileRejections?.length) {
        setValue('document_thumbnail', null, { shouldDirty: true, shouldValidate: false });

        const hasInvalidType = fileRejections.some((rejection) =>
          rejection.errors?.some((error) => error.code === 'file-invalid-type')
        );
        if (hasInvalidType) {
          setError('document_thumbnail', {
            type: 'file-invalid-type',
            message: t('Invalid file type')
          });
          return;
        }

        const tooLargeRejection = fileRejections.find((rejection) =>
          rejection.errors?.some((error) => error.code === 'file-too-large')
        );
        if (tooLargeRejection) {
          setError('document_thumbnail', {
            type: 'file-size',
            message: t(DOCUMENT_THUMBNAIL_MAX_FILE_SIZE_ERROR, {
              defaultValue: DOCUMENT_THUMBNAIL_MAX_FILE_SIZE_ERROR
            })
          });
          return;
        }

        setError('document_thumbnail', {
          type: 'file-invalid-type',
          message: t('Invalid file type')
        });
        return;
      }

      const file = acceptedFiles?.[0];
      if (!file) {
        return;
      }

      if (!isDocumentThumbnailFile(file)) {
        setValue('document_thumbnail', null, { shouldDirty: true, shouldValidate: false });
        setError('document_thumbnail', {
          type: 'file-invalid-type',
          message: t('Invalid file type')
        });
        return;
      }

      if (file.size > DOCUMENT_THUMBNAIL_MAX_FILE_SIZE) {
        setValue('document_thumbnail', null, { shouldDirty: true, shouldValidate: false });
        setError('document_thumbnail', {
          type: 'file-size',
          message: t(DOCUMENT_THUMBNAIL_MAX_FILE_SIZE_ERROR, {
            defaultValue: DOCUMENT_THUMBNAIL_MAX_FILE_SIZE_ERROR
          })
        });
        return;
      }

      clearErrors('document_thumbnail');
      setIsProcessing(true);
      try {
        const compressedImageBlob = await compressImgFile(
          file,
          ImagesOptions(3)[MediaKeys.MEDIA].DIMENSION.WIDTH,
          ImagesOptions(3)[MediaKeys.MEDIA].DIMENSION.HEIGHT
        );
        setValue('document_thumbnail', {
          blob: compressedImageBlob,
          fileName: file.name
        });
      } catch {
        setValue('document_thumbnail', null, { shouldDirty: true, shouldValidate: false });
        setError('document_thumbnail', {
          type: 'file-invalid-type',
          message: t('Invalid file type')
        });
      } finally {
        setIsProcessing(false);
      }
    }
  });

  return (
    <ImageDropzoneContainer key={remountKey} {...getRootProps()}>
      <input {...getInputProps()} />
      <ImageUploadContainer gapY="24px" alignItems="center">
        <CircleContainer $hasImage={false}>
          <ImageIconContainer>
            <ImageIcon />
            <PlusIconWrapper>
              <PlusIcon />
            </PlusIconWrapper>
          </ImageIconContainer>
        </CircleContainer>
        <div>
          <ImageUploadInstructionText>
            {isProcessing
              ? t('Processing thumbnail…', { defaultValue: 'Processing thumbnail…' })
              : t('Drag and drop a thumbnail image', {
                  defaultValue: 'Drag and drop a thumbnail image'
                })}
          </ImageUploadInstructionText>
          <ImageConstraintsText>
            {`${t('max file size', {
              maxFileSize: formatMbFileSize(DOCUMENT_THUMBNAIL_MAX_FILE_SIZE)
            })}. ${t('JPG, JPEG, and PNG supported')}.`}
          </ImageConstraintsText>
        </div>
      </ImageUploadContainer>
    </ImageDropzoneContainer>
  );
}

const DocumentThumbnailUpload = ({
  form,
  existingThumbnailUrl,
  thumbnailRemoved = false,
  onRemoveExisting,
  onRestoreExisting,
  remountKey = 'document-thumbnail',
  clearDisabled = false
}) => {
  const { t } = useTranslation();
  const documentThumbnail = useWatch({ control: form.control, name: 'document_thumbnail' });
  const showExistingThumbnail = Boolean(existingThumbnailUrl) && !thumbnailRemoved;
  const hasNewThumbnail = Boolean(documentThumbnail?.blob);
  const hasThumbnail = showExistingThumbnail || hasNewThumbnail;
  const showRemovedState =
    thumbnailRemoved && Boolean(existingThumbnailUrl) && !hasNewThumbnail;

  const existingPreviewUrl = showExistingThumbnail
    ? resolveDocumentThumbnailUrl(existingThumbnailUrl)
    : '';

  const newPreviewUrl = useMemo(() => {
    if (!hasNewThumbnail) {
      return '';
    }
    return URL.createObjectURL(documentThumbnail.blob);
  }, [documentThumbnail?.blob, hasNewThumbnail]);

  useEffect(() => {
    return () => {
      if (newPreviewUrl) {
        URL.revokeObjectURL(newPreviewUrl);
      }
    };
  }, [newPreviewUrl]);

  const previewImageUrl = newPreviewUrl || existingPreviewUrl;

  const handleClearThumbnail = () => {
    if (clearDisabled) {
      return;
    }
    form.clearErrors('document_thumbnail');
    if (hasNewThumbnail) {
      form.setValue('document_thumbnail', null);
      return;
    }
    if (onRemoveExisting) {
      onRemoveExisting();
    }
    form.setValue('document_thumbnail', null);
  };

  const uploadField = (
    <FormField
      control={form.control}
      name="document_thumbnail"
      render={() => (
        <>
          <ThumbnailDropzone form={form} t={t} remountKey={remountKey} />
          <FormMessage />
        </>
      )}
    />
  );

  return (
    <FormItem alt style={{ marginBottom: 0 }}>
      <DocumentSection>
        <DocumentSectionHeading>
          {t('Thumbnail', { defaultValue: 'Thumbnail' })}
        </DocumentSectionHeading>
        {hasThumbnail ? (
          <>
            <DocumentUploadedFileRow
              t={t}
              file={
                hasNewThumbnail
                  ? {
                      name: documentThumbnail.fileName,
                      size: documentThumbnail.blob.size
                    }
                  : null
              }
              fileName={
                hasNewThumbnail
                  ? undefined
                  : getThumbnailDisplayName(existingThumbnailUrl) ||
                    t('Thumbnail', { defaultValue: 'Thumbnail' })
              }
              previewImageUrl={previewImageUrl}
              onClearFile={handleClearThumbnail}
              clearDisabled={clearDisabled}
            />
            <FormField
              control={form.control}
              name="document_thumbnail"
              render={() => <FormMessage />}
            />
          </>
        ) : showRemovedState ? (
          <>
            {uploadField}
            {onRestoreExisting ? (
              <DeleteAvatarButton type="button" onClick={onRestoreExisting}>
                {t('Restore thumbnail', { defaultValue: 'Restore thumbnail' })}
              </DeleteAvatarButton>
            ) : null}
          </>
        ) : (
          uploadField
        )}
      </DocumentSection>
    </FormItem>
  );
};

export default DocumentThumbnailUpload;
